import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { BlogDetailsPageByPathDocument, BlogDetailsPageFragment, Page as IPage } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { BlogDetailsPage, ChatBot, NextPageHead, PageView, VWOExperimentPush } from '@src/lib/shared-components-web';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { fetchAppData } from '@utils/pages/app-data-fetcher';
import choosePreview from '@utils/pages/choose-preview';
import { describeContentfulError, throwIfContentfulUnavailable } from '@utils/pages/contentful-errors';
import convertPathToBreadCrumbTitle from '@utils/pages/convert-path-to-breadcrumb-title';
import DetermineIndexing from '@utils/pages/determine-indexing';
import getConfigByBrand from '@utils/pages/get-config-by-brand';
import getConfigServices from '@utils/pages/get-config-services';
import { getDeviceTypeFromHeaders } from '@utils/pages/get-device-type-from-headers';
import getHostFromHeaders from '@utils/pages/get-host-from-headers';
import { getServerSideTranslations } from '@utils/pages/get-serverside-translations';
import getThemeByDomainName from '@utils/pages/get-theme-by-domain-name';
import { handlePageLoadFailure } from '@utils/pages/handle-page-load-failure';
import resolveGeoCoordinates, { CloudfrontGeoHeaders } from '@utils/pages/resolve-geo-coordinates';
import resolveTagTheme from '@utils/pages/resolve-tag-theme';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    url: logoUrl
  } = props.appData?.logo || {};
  const {
    pageData,
    path,
    breadcrumbsData,
    preview,
    domain
  } = props;
  const {
    metaTitle,
    metaDescription,
    canonical,
    indexed,
    analyticsContext,
    ogImage
  } = pageData;
  return <>
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} data-sentry-element="VWOExperimentPush" data-sentry-source-file="[...slug].page.tsx" />
			<PageView analyticsContext={analyticsContext} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
			<NextPageHead path={path} title={metaTitle} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={metaDescription} canonical={canonical} originUrl={siteURL} breadcrumbTitle={breadcrumbsData.breadcrumbTitle} breadcrumbs={breadcrumbsData.breadcrumbsCollection.items} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
			<BlogDetailsPage {...pageData} data-sentry-element="BlogDetailsPage" data-sentry-source-file="[...slug].page.tsx" />
			{features.chat.enabled && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</>;
};
export const getServerSideProps: GetServerSideProps = async ({
  resolvedUrl,
  params,
  query,
  locale,
  req,
  res,
  preview = false
}) => {
  if (!params?.slug) {
    return {
      notFound: true
    };
  }
  const domain = getHostFromHeaders(req?.headers);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const theme = getThemeByDomainName(domain);
  const tagTheme = resolveTagTheme(req.headers, theme);
  const logger = getLogger('blog-detail-page', tagTheme);
  try {
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.warn('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const appData = await fetchAppData(gqlClient, appName, preview, logger);
    if (!appData) {
      return {
        notFound: true
      };
    }
    const slugs = params.slug as string[];

    // this path value needed for page indexing
    const path = '/' + slugs.join('/');
    logger.debug(path);

    //only the last section in the url path is the slug to query
    const pathForQuery = slugs.at(-1);
    const title = brandConfig.name;
    const pageData = await gqlClient.query(BlogDetailsPageByPathDocument, {
      path: pathForQuery,
      title,
      preview
    }).toPromise();
    throwIfContentfulUnavailable(pageData, 'CONTENTFUL_ERROR_BLOG_DETAILS_PAGE');
    if (pageData.error) {
      logger.warn(`CONTENTFUL_ERROR_BLOG_DETAILS_PAGE: ${describeContentfulError(pageData.error)}`);
    }
    const page = pageData.data?.blogDetailsPageCollection?.items[0] as BlogDetailsPageFragment;
    if (!page) {
      logger.warn(`COULD_NOT_FIND_THE_BLOG_SLUG: ${pathForQuery}`);
      return {
        notFound: true
      };
    }

    // breadcrumb data generation for blog detail page
    const {
      features: {
        blogs: {
          root
        }
      }
    } = brandConfig;
    const breadcrumbsData = {
      // current page title from the slug value
      breadcrumbTitle: convertPathToBreadCrumbTitle(slugs.at(-1)),
      // current page path
      path,
      // just blog list page in the hierarchy now
      // need to add category pages once that is built
      breadcrumbsCollection: {
        items: [{
          breadcrumbTitle: 'Home',
          path: '/'
        } as IPage, {
          breadcrumbTitle: convertPathToBreadCrumbTitle(root),
          path: root
        } as IPage]
      }
    };
    const {
      hasIpBasedCoordinates,
      coordinates
    } = resolveGeoCoordinates(req.headers as CloudfrontGeoHeaders);
    if (hasIpBasedCoordinates) {
      logger.debug(`CDN_GEO_LOCATION_FOUND: [${coordinates.latitude}, ${coordinates.longitude}]`);
    } else {
      logger.debug(`CDN_GEO_LOCATION_MISS`);
    }
    const transformedServices = getConfigServices(brandConfig as any);

    // Resolve standalone VWO FME flags (layout-level header swap, etc.)
    const {
      flags: serverFeatureFlags,
      decisions: vwoDecisions
    } = await resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, brandConfig as any, req, res, query, logger);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        pageData: page,
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        appData,
        path,
        breadcrumbsData,
        preview,
        deviceType,
        domain: domain ?? '',
        serverFeatureFlags,
        vwoDecisions
      }
    };
  } catch (e) {
    return handlePageLoadFailure(logger, resolvedUrl, e);
  }
};
export default Page;